import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../utils/apiFetch";
import { monthKey } from "../utils/dateOnly";

const queryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const output = search.toString();
  return output ? `?${output}` : "";
};

const reflectionFields = ["stands_out", "god_is_showing", "response", "prayer", "diary"];

const reflectionPayload = (entry = {}) => reflectionFields.reduce((payload, field) => {
  payload[field] = entry[field] ?? "";
  return payload;
}, { revision: entry.revision ?? null });

export const fetchDevotionDay = createAsyncThunk(
  "devotions/fetchDay",
  async ({ date, version, language } = {}, { rejectWithValue }) => {
    try {
      const path = date ? `/api/devotions/date/${date}` : "/api/devotions/today";
      return await apiFetch(`${path}${queryString({ version, language })}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const saveDevotionEntry = createAsyncThunk(
  "devotions/saveEntry",
  async ({ dailyPassageId, entry }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/entries/${dailyPassageId}`, {
        method: "PUT",
        body: JSON.stringify(reflectionPayload(entry)),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const completeDevotionEntry = createAsyncThunk(
  "devotions/completeEntry",
  async ({ dailyPassageId, isComplete = true }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/entries/${dailyPassageId}/complete`, {
        method: "POST",
        body: JSON.stringify({ is_complete: isComplete }),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const fetchDevotionHistory = createAsyncThunk(
  "devotions/fetchHistory",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/history${queryString(params)}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const fetchDevotionCalendar = createAsyncThunk(
  "devotions/fetchCalendar",
  async ({ year, month, language = "en" }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/calendar${queryString({ year, month, language })}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const fetchWeeklyPreparation = createAsyncThunk(
  "devotions/fetchWeek",
  async (weekStart, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/weeks/${weekStart}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const updateWeeklyPreparation = createAsyncThunk(
  "devotions/updateWeek",
  async ({ weekStart, preparation }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/weeks/${weekStart}`, {
        method: "PUT",
        body: JSON.stringify(preparation),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const addWeeklyPreparationItem = createAsyncThunk(
  "devotions/addWeekItem",
  async ({ weekStart, devotionEntryId, shareText }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/weeks/${weekStart}/items`, {
        method: "POST",
        body: JSON.stringify({ devotion_entry_id: devotionEntryId, share_text: shareText }),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const updateWeeklyPreparationItem = createAsyncThunk(
  "devotions/updateWeekItem",
  async ({ weekStart, itemId, item }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/devotions/weeks/${weekStart}/items/${itemId}`, {
        method: "PUT",
        body: JSON.stringify(item),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const deleteWeeklyPreparationItem = createAsyncThunk(
  "devotions/deleteWeekItem",
  async ({ weekStart, itemId }, { rejectWithValue }) => {
    try {
      await apiFetch(`/api/devotions/weeks/${weekStart}/items/${itemId}`, { method: "DELETE" });
      return { itemId };
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const fetchPastorContentList = createAsyncThunk(
  "devotions/fetchPastorContentList",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/admin/devotions/content${queryString(params)}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const fetchPastoralContent = createAsyncThunk(
  "devotions/fetchPastoralContent",
  async ({ dailyPassageId, language }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/admin/devotions/content/${dailyPassageId}/${language}`);
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const savePastoralContent = createAsyncThunk(
  "devotions/savePastoralContent",
  async ({ dailyPassageId, language, content }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/admin/devotions/content/${dailyPassageId}/${language}`, {
        method: "PUT",
        body: JSON.stringify(content),
      });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const publishPastoralContent = createAsyncThunk(
  "devotions/publishPastoralContent",
  async (contentId, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/admin/devotions/content/${contentId}/publish`, { method: "POST" });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

export const unpublishPastoralContent = createAsyncThunk(
  "devotions/unpublishPastoralContent",
  async (contentId, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/admin/devotions/content/${contentId}/unpublish`, { method: "POST" });
    } catch (err) {
      return rejectWithValue(err.data || { error: err.message, status: err.status });
    }
  }
);

const devotionSlice = createSlice({
  name: "devotions",
  initialState: {
    currentDay: null,
    history: { items: [], page: 1, pages: 0, total: 0 },
    calendar: { byMonth: {}, loadingByMonth: {}, errorByMonth: {} },
    weeklyPreparation: null,
    pastorContentList: [],
    pastorContentDraft: null,
    loading: false,
    saveStatus: "idle",
    error: null,
    conflict: null,
  },
  reducers: {
    clearDevotionError(state) {
      state.error = null;
      state.conflict = null;
    },
    setSaveStatus(state, action) {
      state.saveStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDevotionDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevotionDay.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDay = action.payload;
        state.saveStatus = "idle";
      })
      .addCase(fetchDevotionDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Could not load devotion";
      })
      .addCase(saveDevotionEntry.pending, (state) => {
        state.saveStatus = "saving";
      })
      .addCase(saveDevotionEntry.fulfilled, (state, action) => {
        state.saveStatus = "saved";
        if (state.currentDay) state.currentDay.entry = action.payload;
        Object.values(state.calendar.byMonth).forEach((calendar) => {
          const day = calendar.days.find((item) => item.passages.some((passage) => passage.id === action.meta.arg.dailyPassageId));
          if (day) day.status = action.payload.is_complete ? "complete" : "started";
        });
      })
      .addCase(saveDevotionEntry.rejected, (state, action) => {
        state.saveStatus = "error";
        if (action.payload?.entry) state.conflict = action.payload.entry;
        state.error = action.payload?.error || "Could not save";
      })
      .addCase(completeDevotionEntry.fulfilled, (state, action) => {
        if (state.currentDay) state.currentDay.entry = action.payload;
        const passageId = action.meta.arg.dailyPassageId;
        const nextStatus = action.payload.is_complete ? "complete" : "started";
        Object.values(state.calendar.byMonth).forEach((calendar) => {
          const day = calendar.days.find((item) => item.passages.some((passage) => passage.id === passageId));
          if (day) day.status = nextStatus;
        });
      })
      .addCase(fetchDevotionCalendar.pending, (state, action) => {
        const { year, month, language = "en" } = action.meta.arg;
        state.calendar.loadingByMonth[monthKey(year, month, language)] = true;
        state.calendar.errorByMonth[monthKey(year, month, language)] = null;
      })
      .addCase(fetchDevotionCalendar.fulfilled, (state, action) => {
        const { year, month, language = "en" } = action.meta.arg;
        const key = monthKey(year, month, language);
        state.calendar.byMonth[key] = action.payload;
        state.calendar.loadingByMonth[key] = false;
      })
      .addCase(fetchDevotionCalendar.rejected, (state, action) => {
        const { year, month, language = "en" } = action.meta.arg;
        const key = monthKey(year, month, language);
        state.calendar.loadingByMonth[key] = false;
        state.calendar.errorByMonth[key] = action.payload?.error || "Could not load calendar";
      })
      .addCase(fetchDevotionHistory.fulfilled, (state, action) => {
        state.history = action.payload;
      })
      .addCase(fetchWeeklyPreparation.fulfilled, (state, action) => {
        state.weeklyPreparation = action.payload;
      })
      .addCase(updateWeeklyPreparation.fulfilled, (state, action) => {
        state.weeklyPreparation = action.payload;
      })
      .addCase(addWeeklyPreparationItem.fulfilled, (state, action) => {
        if (state.weeklyPreparation?.preparation?.items) {
          state.weeklyPreparation.preparation.items.push(action.payload);
        }
      })
      .addCase(updateWeeklyPreparationItem.fulfilled, (state, action) => {
        const items = state.weeklyPreparation?.preparation?.items || [];
        const index = items.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) items[index] = action.payload;
      })
      .addCase(deleteWeeklyPreparationItem.fulfilled, (state, action) => {
        const items = state.weeklyPreparation?.preparation?.items || [];
        state.weeklyPreparation.preparation.items = items.filter((item) => item.id !== action.payload.itemId);
      })
      .addCase(fetchPastorContentList.fulfilled, (state, action) => {
        state.pastorContentList = action.payload;
      })
      .addCase(fetchPastoralContent.fulfilled, (state, action) => {
        state.pastorContentDraft = action.payload;
      })
      .addCase(savePastoralContent.fulfilled, (state, action) => {
        if (state.pastorContentDraft) state.pastorContentDraft.content = action.payload;
      })
      .addCase(publishPastoralContent.fulfilled, (state, action) => {
        if (state.pastorContentDraft) state.pastorContentDraft.content = action.payload;
      })
      .addCase(unpublishPastoralContent.fulfilled, (state, action) => {
        if (state.pastorContentDraft) state.pastorContentDraft.content = action.payload;
      });
  },
});

export const { clearDevotionError, setSaveStatus } = devotionSlice.actions;
export default devotionSlice.reducer;
